/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

/* eslint-env browser */

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';
import isObject from 'lodash/isObject';

/**
 * Analytický cyklus.
 * Spravuje sběr analytických dat do skupin v daných intervalech.
 * @implements {Destroyable}
 * @implements {EventBus}
 */
class AnalyticsCycle {
  /**
   * Vytvoří instanci.
   * @param {Store} store Datový sklad s analytickými daty.
   * @returns {AnalyticsCycle} Nová instance.
   */
  constructor(store) {
    // Validace argumentů.
    if (!isObject(store)) {
      throw new TypeError(`Invalid store "${store}".`);
    }
    /**
     * Skupina svázaná s aktuálně spuštěným analytickým cyklem.
     * Hodnota je zaručeně nastavena, pokud je cyklus spuštěný.
     * @readonly
     * @type {?AnalyticsGroup}
     */
    this.analyticsGroup = null;
    /**
     * Doba, kterou trvá perioda aktuálně spuštěného analytického cyklu. [ms]
     * Hodnota je zaručeně nastavena, pokud je cyklus spuštěný.
     * @readonly
     * @type {?number}
     */
    this.analyticsPeriod = null;
    /**
     * Správce událostí.
     * @private
     * @type {EventEmitter}
     */
    this.emitter = new EventEmitter();
    /**
     * Datový sklad s analytickými daty.
     * @private
     * @type {Store}
     */
    this.store = store;
    /**
     * Časovač s naplánovaným ukončením spuštěného analytického cyklu.
     * @private
     * @type {?number}
     */
    this.timeout = null;
    // Navázání naslouchání na instanci.
    this.onError = this.onError.bind(this);
    this.onTimeout = this.onTimeout.bind(this);
  }

  /**
   * Zruší naplánovaný konec cyklu.
   * @private
   * @returns {undefined} Nic.
   */
  cancelTimeout() {
    const { timeout } = this;
    // Kontrola nastavení timeoutu ke zrušení.
    if (!timeout) {
      throw new Error('Timeout is not set.');
    }
    // Přerušení časovače ukončení cyklu.
    clearTimeout(timeout);
    this.timeout = null;
  }

  /**
   * Uzavře analytickou skupinu.
   * @param {AnalyticsGroup} analyticsGroup Skupina, která se má uzavřít.
   * @returns {Promise.<AnalyticsGroup>} Vyřeší se po uzavření.
   */
  closeGroup(analyticsGroup) {
    return new Promise((resolve) =>
      resolve(
        this.store.update('analyticsGroup', analyticsGroup.id, {
          // Nastavení closed dle aktuálního času.
          closed: new Date().toISOString()
        })
      )
    );
  }

  /** @inheritdoc */
  destroy() {
    clearTimeout(this.timeout);
  }

  /**
   * Zkontroluje, jestli je analytický cyklus spuštěný.
   * Spuštěný cyklus zaručuje přítomnost `analyticsGroup` a `analyticsPeriod`.
   * @returns {boolean} Příznak, že je cyklus spuštěný.
   */
  isStarted() {
    // Pro kontrolu se používá analytická skupina, která je vždy nastavená.
    // Kdyby nastavená nebyla, tak by docházelo k ignoraci.
    // Pozor na timeout, který v některých okamžicích (např. restart) nemusí být nastavený.
    return !isNil(this.analyticsGroup);
  }

  /** @inheritdoc */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @inheritdoc */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na chybu.
   * @fires AnalyticsCycle#error
   * @param {Error} error Chybový objekt.
   * @private
   * @returns {undefined} Nic.
   */
  onError(error) {
    this.emitter.emit('error', error);
  }

  /**
   * Reakce na uplynutí konce cyklu.
   * Ukončí probíhající iteraci cyklu a spustí novou.
   * @fires AnalyticsCycle#ended
   * @fires AnalyticsCycle#error
   * @private
   * @returns {undefined} Nic.
   */
  onTimeout() {
    try {
      // Reset identifikátoru právě vyvolaného timeout.
      this.timeout = null;
      // Restart skončené iterace.
      this.restartIteration(this.analyticsPeriod).catch(this.onError);
    } catch (error) {
      this.onError(error);
    }
  }

  /**
   * Otevře novou analytickou skupinu.
   * @returns {Promise.<AnalyticsGroup>} Vyřeší se po otevření.
   */
  openGroup() {
    return new Promise((resolve) =>
      resolve(
        this.store.create('analyticsGroup', {
          // Nastavení opened dle aktuálního času.
          opened: new Date().toISOString()
        })
      )
    );
  }

  /**
   * Předčasně zastaví probíhající analytický cyklus a spustí nový.
   * Narozdíl od odděleného zastavení a spuštění nepřeruší nastavení navázané skupiny.
   * @fires AnalyticsCycle#ended
   * @fires AnalyticsCycle#error
   * @param {number} analyticsPeriod Doba, kterou trvá analytický cyklus. [ms]
   * @returns {Promise.<undefined>} Vyřeší se po spuštění analytického cyklu.
   */
  restart(analyticsPeriod) {
    return new Promise((resolve) => {
      // Kontrola stavu cyklu.
      if (!this.isStarted()) {
        throw new Error('Cycle is not in progress.');
      }
      // Zrušení naplánovaného konce iterace.
      this.cancelTimeout();
      // Restart probíhající iterace.
      return resolve(this.restartIteration(analyticsPeriod));
    });
  }

  /**
   * Zastaví probíhající iteraci analytického cyklu a spustí novou.
   * @fires AnalyticsCycle#ended
   * @fires AnalyticsCycle#error
   * @param {number} analyticsPeriod Doba, kterou trvá analytický cyklus. [ms]
   * @private
   * @returns {Promise.<undefined>} Vyřeší se po spuštění iterace analytického cyklu.
   */
  restartIteration(analyticsPeriod) {
    // TODO #121 Ošetřit opakované spouštění.
    return new Promise((resolve) => {
      // Uložení ukončované analytické skupiny.
      const oldAnalyticsGroup = this.analyticsGroup;
      return resolve(
        // Uzavření ukončované analytické skupiny.
        this.closeGroup(oldAnalyticsGroup)
          // Otevření nové analytické skupiny.
          .then(() => this.openGroup())
          // Synchronní výměna ukončované analytické skupiny za novou.
          // Je totiž nutné mít neustále nastavenou nějakou aktivní skupinu.
          .then((newAnalyticsGroup) => {
            // Nastavení aktivní skupiny cyklu.
            this.analyticsGroup = newAnalyticsGroup;
            // Uložení periody pro další iterace cyklu.
            this.analyticsPeriod = analyticsPeriod;
            // Naplánování konce nové iterace cyklu.
            this.startTimeout(analyticsPeriod);
            // Vyvolání události po dokončení výměny.
            this.emitter.emit('ended', oldAnalyticsGroup);
          })
      );
    });
  }

  /**
   * Spustí nový analytický cyklus.
   * @fires AnalyticsCycle#ended
   * @fires AnalyticsCycle#error
   * @param {number} analyticsPeriod Doba, kterou trvá analytický cyklus. [ms]
   * @returns {Promise.<undefined>} Vyřeší se po spuštění analytického cyklu.
   */
  start(analyticsPeriod) {
    // TODO #121 Ošetřit opakované spouštění.
    return new Promise((resolve) => {
      // Kontrola stavu cyklu.
      if (this.isStarted()) {
        throw new Error('Another cycle is in progress.');
      }
      // Start první iterace analytického cyklu.
      return resolve(
        // Otevření nové analytické skupiny.
        this.openGroup()
          // Spuštění iterace analytického cyklu.
          .then((analyticsGroup) => {
            // Nastavení aktivní skupiny cyklu.
            this.analyticsGroup = analyticsGroup;
            // Uložení periody pro další iterace cyklu.
            this.analyticsPeriod = analyticsPeriod;
            // Naplánování konce iterace cyklu.
            this.startTimeout(analyticsPeriod);
          })
      );
    });
  }

  /**
   * Naplánuje konec jedné iterace analytického cyklu.
   * @fires AnalyticsCycle#ended
   * @fires AnalyticsCycle#error
   * @param {number} analyticsPeriod Doba, kterou trvá analytický cyklus. [ms]
   * @private
   * @returns {undefined} Nic.
   */
  startTimeout(analyticsPeriod) {
    // Kontrola duplikátního spuštění timeoutu.
    if (this.timeout) {
      throw new Error('Timeout is already set.');
    }
    // Kontrola periody cyklu.
    // Pro jistotu se používá ošetření minimální i maximální hodnoty pro prevenci zacyklení s `delay = 0`.
    // Více informací https://stackoverflow.com/questions/3468607/why-does-settimeout-break-for-large-millisecond-delay-values
    // TODO Zvážit obecné řešení tohoto problému jako v jiných projektech.
    if (!Number.isFinite(analyticsPeriod)) {
      throw new TypeError(`Invalid cycle period "${analyticsPeriod}".`);
    }
    if (!(analyticsPeriod > 0 && analyticsPeriod < 0x7fffffff)) {
      throw new RangeError(
        `Cycle period "${analyticsPeriod}" is out of range.`
      );
    }
    this.timeout = setTimeout(this.onTimeout, analyticsPeriod);
  }

  /**
   * Předčasně zastaví spuštěný analytický cyklus.
   * @fires AnalyticsCycle#ended
   * @returns {Promise.<undefined>} Vyřeší se po zastavení analytického cyklu.
   */
  stop() {
    return new Promise((resolve) => {
      // Kontrola stavu cyklu.
      if (!this.isStarted()) {
        throw new Error('Cycle is not in progress.');
      }
      // Zrušení naplánovaného konce iterace.
      this.cancelTimeout();
      // Uložení ukončované analytické skupiny.
      const { analyticsGroup } = this;
      // Reset analytické skupiny a periody do výchozího stavu.
      this.analyticsGroup = null;
      this.analyticsPeriod = null;
      return resolve(
        // Uzavření ukončované analytické skupiny.
        this.closeGroup(analyticsGroup)
          // Vyvolání události po dokončení výměny.
          .then(() => {
            this.emitter.emit('ended', analyticsGroup);
          })
      );
    });
  }
}

/**
 * Konec jedné iterace analytického cyklu.
 * Nebere ohled na to, jestli byl cyklus přerušený předběžně, nebo po uplyutí požadované periody.
 * @event AnalyticsCycle#ended
 * @param {AnalyticsGroup} analyticsGroup Analytická skupina ukončeného cyklu.
 */

/**
 * Chybová událost.
 * @event AnalyticsCycle#error
 * @param {Error} error Chybové hlášení.
 */

export default AnalyticsCycle;
